// Fase 17.3, Plano 06 (D-25) — "produto sem cadastro não pode ser esquecido": helpers e chamadas do "Cadastrar peça".
// Dois jeitos de chegar à tela: pelo item do PV (?pedidoVendaId=&itemId=) ou pelo código do cliente (?clienteId=&codigo=).
import type {
  AtalhoCadastroDto,
  CadastrarPecaDoCodigoDto,
  CadastrarPecaDoItemDto,
  ItemSemCadastroDto,
  MaterialCadastrarPecaDto,
  PedidoVendaItemView,
  ResultadoCadastrarPecaDoCodigoDto,
  ResultadoCadastrarPecaDto,
  SalvarRoteiroPecaDto,
} from '@pieltec/contracts';
import { apiFetch, apiMutate } from '../csrf-fetch';
import { lerErroApi } from '../vendas/erro-api';
import { ErroRoteiroApi } from './roteiros-api';

export const ROTULO_PRODUTO_SEM_CADASTRO = 'Produto sem cadastro';
export const ROTULO_CADASTRAR_PECA = 'Cadastrar peça';
export const ROTULO_CADASTRAR_ROTEIRO = 'Cadastrar roteiro';
export const PERMISSAO_CADASTRAR = 'roteiros:gerenciar';
export const PERMISSAO_EDITAR_PV = 'pv:editar';
export const PERMISSAO_CRIAR_MATERIAL = 'cadastros:gerenciar';
/** `/pcp/roteiros` (lista e ficha da peça). */
export const PERMISSAO_VER_ROTEIROS = 'producao:consultar';
/** Resolver a pendência ou ligar o código do cliente a um material já cadastrado é aprovar vínculo (P2-CR-01). */
export const PERMISSAO_APROVAR_VINCULO = 'remessas:gerenciar';

export const MENSAGEM_ITEM_ALTERADO = 'O item do pedido foi alterado. Recarregue e tente de novo.';
export const MENSAGEM_PECA_CADASTRADA = 'Peça cadastrada e ligada ao pedido.';
export const MENSAGEM_PECA_CADASTRADA_PELO_CODIGO = 'Peça cadastrada e vínculo criado.';
export const MENSAGEM_PENDENCIA_RESOLVIDA = 'Peça cadastrada e vínculo criado; a pendência foi resolvida.';
export const MENSAGEM_SEM_PERMISSAO_MATERIAL_NOVO = 'Para criar um material novo é preciso a permissão cadastros:gerenciar. Use um material já cadastrado ou peça a um Admin.';
export const MENSAGEM_SEM_PERMISSAO_CADASTRAR =
  `Você não tem permissão para cadastrar a peça. É preciso ${PERMISSAO_CADASTRAR}, ${PERMISSAO_EDITAR_PV} (pelo item do pedido) e, para criar um material novo, ${PERMISSAO_CRIAR_MATERIAL}. `
  + `Resolver a pendência ou ligar o código do cliente a um material já cadastrado exige também ${PERMISSAO_APROVAR_VINCULO}.`;
export const MENSAGEM_SEM_PERMISSAO_VINCULAR =
  `Para resolver a pendência ou ligar o código do cliente a um material já cadastrado é preciso a permissão ${PERMISSAO_APROVAR_VINCULO}. `
  + 'Sem ela, só é possível criar um material novo, sem pendência.';
export const MENSAGEM_BLOQUEIO_SEM_CADASTRO = 'Produto sem cadastro: cadastre a peça (Cadastrar peça) antes de gerar a OP.';
export const MENSAGEM_BLOQUEIO_SEM_ROTEIRO = 'Peça sem roteiro: cadastre o roteiro da peça (Cadastrar roteiro) antes de gerar a OP.';
export const NOTA_PENDENCIA_REGISTRADA = 'A pendência ficou registrada na Central de Pendências.';
export const NOTA_MATERIAL_DA_OMIE = 'Se este produto também vier da Omie com o mesmo código, o cadastro é reaproveitado: pedidos, vínculo e roteiro continuam valendo, e a carga só atualiza nome e unidade.';

export const CODIGO_PV_ITEM_SEM_CADASTRO = 'PV_ITEM_SEM_CADASTRO';
export const CODIGO_PV_ITEM_SEM_ROTEIRO = 'PV_ITEM_SEM_ROTEIRO';
export const CODIGO_REMESSA_ITEM_SEM_CADASTRO = 'REMESSA_ITEM_SEM_CADASTRO';

const BASE_VENDAS = '/api/v1/vendas';
const ROTA_NOVA_PECA = '/pcp/roteiros/nova';

// ---------------------------------------------------------------------------------------------
// Entrada da tela e valores iniciais
// ---------------------------------------------------------------------------------------------

export type ModoNovaPeca = 'ITEM_PV' | 'CODIGO_CLIENTE' | 'LISTA';

export interface ParametrosNovaPeca {
  readonly pedidoVendaId: string | null;
  readonly itemId: string | null;
  readonly clienteId: string | null;
  readonly codigo: string | null;
  readonly pendenciaId: string | null;
  readonly descricao: string | null;
  /** Nome do cliente quando o link o informa (o cadastro de clientes não tem busca por id). */
  readonly clienteNome: string | null;
}

export function parametrosDaNovaPeca(consulta: string): ParametrosNovaPeca {
  const params = new URLSearchParams(consulta);
  const ler = (nome: string): string | null => params.get(nome)?.trim() || null;
  return {
    pedidoVendaId: ler('pedidoVendaId'), itemId: ler('itemId'), clienteId: ler('clienteId'),
    codigo: ler('codigo'), pendenciaId: ler('pendenciaId'), descricao: ler('descricao'), clienteNome: ler('clienteNome'),
  };
}

/** ITEM_PV com os dois ids do PV; CODIGO_CLIENTE com cliente e código; senão a lista de produtos sem cadastro. */
export function modoDaNovaPeca(consulta: string): ModoNovaPeca {
  const parametros = parametrosDaNovaPeca(consulta);
  if (parametros.pedidoVendaId && parametros.itemId) return 'ITEM_PV';
  if (parametros.clienteId && parametros.codigo) return 'CODIGO_CLIENTE';
  return 'LISTA';
}

export interface ValoresIniciaisPeca {
  readonly codigo: string;
  readonly nome: string;
  readonly unidade: string;
  readonly cor: string;
}

/** Pré-preenchimento da "Nova peça": código = código do cliente, nome = descrição, unidade e cor do item. */
export function valoresIniciaisDoItem(item: Pick<ItemSemCadastroDto, 'codigoItemCliente' | 'descricao' | 'unidade' | 'cor'> | Pick<PedidoVendaItemView, 'codigoItemCliente' | 'descricao' | 'unidade' | 'cor'>): ValoresIniciaisPeca {
  return { codigo: item.codigoItemCliente.trim(), nome: item.descricao.trim(), unidade: item.unidade.trim() || 'un', cor: item.cor?.trim() ?? '' };
}

// ---------------------------------------------------------------------------------------------
// Corpo do POST
// ---------------------------------------------------------------------------------------------

export interface FormularioMaterial {
  readonly modo: 'EXISTENTE' | 'NOVA';
  readonly materialIdExistente: string;
  readonly codigo: string;
  readonly nome: string;
  readonly unidade: string;
  readonly cor: string;
}

/** `materialIdExistente` OU código + nome + unidade (nunca os dois), como a API exige. */
export function materialParaEnviar(formulario: FormularioMaterial): MaterialCadastrarPecaDto {
  if (formulario.modo === 'EXISTENTE') return { materialIdExistente: formulario.materialIdExistente };
  const cor = formulario.cor.trim();
  return { codigo: formulario.codigo.trim(), nome: formulario.nome.trim(), unidade: formulario.unidade.trim(), ...(cor ? { corCodigo: cor } : {}) };
}

/** Mensagens em português do que falta no formulário; vazio = pode enviar. */
export function validarFormularioMaterial(formulario: FormularioMaterial): string[] {
  if (formulario.modo === 'EXISTENTE') return formulario.materialIdExistente ? [] : ['Escolha o material já cadastrado.'];
  const mensagens: string[] = [];
  if (!/^[A-Za-z0-9][A-Za-z0-9 ./_-]{0,59}$/.test(formulario.codigo.trim())) mensagens.push('Informe o código da peça (letras, números e . / _ -, até 60 caracteres).');
  if (formulario.nome.trim().length < 2) mensagens.push('Informe o nome da peça (mínimo de 2 caracteres).');
  if (formulario.unidade.trim() === '' || formulario.unidade.trim().length > 10) mensagens.push('Informe a unidade (até 10 caracteres).');
  return mensagens;
}

export function corpoCadastrarPecaDoItem(itemVersao: number, material: MaterialCadastrarPecaDto, roteiro: SalvarRoteiroPecaDto | null): CadastrarPecaDoItemDto {
  return { itemVersao, material, ...(roteiro ? { roteiro } : {}) };
}

export function corpoCadastrarPecaDoCodigo(parametros: Pick<ParametrosNovaPeca, 'clienteId' | 'codigo' | 'pendenciaId' | 'descricao'>, material: MaterialCadastrarPecaDto, roteiro: SalvarRoteiroPecaDto | null): CadastrarPecaDoCodigoDto {
  return {
    clienteId: parametros.clienteId ?? '',
    codigoItemCliente: parametros.codigo ?? '',
    ...(parametros.descricao ? { descricao: parametros.descricao } : {}),
    ...(parametros.pendenciaId ? { pendenciaId: parametros.pendenciaId } : {}),
    material,
    ...(roteiro ? { roteiro } : {}),
  };
}

// ---------------------------------------------------------------------------------------------
// Atalhos e mensagens de bloqueio (D-25a/b/c)
// ---------------------------------------------------------------------------------------------

export interface AtalhoDaTela {
  readonly href: string;
  readonly rotulo: string;
}

export function linkCadastrarPecaDoItem(pedidoVendaId: string, itemId: string): string {
  return `${ROTA_NOVA_PECA}?pedidoVendaId=${encodeURIComponent(pedidoVendaId)}&itemId=${encodeURIComponent(itemId)}`;
}

export function linkCadastrarPecaDoCodigo(clienteId: string, codigo: string, pendenciaId?: string | null, clienteNome?: string | null): string {
  const base = `${ROTA_NOVA_PECA}?clienteId=${encodeURIComponent(clienteId)}&codigo=${encodeURIComponent(codigo)}`;
  const comPendencia = pendenciaId ? `${base}&pendenciaId=${encodeURIComponent(pendenciaId)}` : base;
  return clienteNome ? `${comPendencia}&clienteNome=${encodeURIComponent(clienteNome)}` : comPendencia;
}

/** Detalhe do PV (a rota usa `?id=`). */
export function linkDoPedido(pedidoVendaId: string): string {
  return `/vendas/pedidos/detalhe?id=${encodeURIComponent(pedidoVendaId)}`;
}

export function linkDaFichaDaPeca(materialId: string): string {
  return `/pcp/roteiros/${encodeURIComponent(materialId)}`;
}

/** Atalho do `atalho` devolvido pela API junto do 409/400; null quando não há para onde levar o PCP. */
export function atalhoDoErro(erro: { readonly codigo: string | null; readonly atalho: AtalhoCadastroDto | null }): AtalhoDaTela | null {
  const atalho = erro.atalho;
  if (!atalho) return null;
  if (atalho.tipo === 'CADASTRAR_ROTEIRO') {
    return atalho.materialId ? { href: linkDaFichaDaPeca(atalho.materialId), rotulo: ROTULO_CADASTRAR_ROTEIRO } : null;
  }
  if (atalho.pedidoVendaId && atalho.itemId) return { href: linkCadastrarPecaDoItem(atalho.pedidoVendaId, atalho.itemId), rotulo: ROTULO_CADASTRAR_PECA };
  if (atalho.clienteId && atalho.codigoItemCliente) return { href: linkCadastrarPecaDoCodigo(atalho.clienteId, atalho.codigoItemCliente, atalho.pendenciaId), rotulo: ROTULO_CADASTRAR_PECA };
  return null;
}

export interface AtalhoComPermissao extends AtalhoDaTela {
  /** Permissões que o destino exige (rota da tela e API de gravação); o link só aparece quando todas estão na sessão. */
  readonly exige: readonly string[];
}

/** O que o destino do atalho da API exige: ficha da peça = ver roteiros; cadastrar peça = gerenciar roteiros (+ pv:editar pelo item, + vínculo pela pendência). */
export function permissoesDoAtalho(atalho: AtalhoCadastroDto): readonly string[] {
  if (atalho.tipo === 'CADASTRAR_ROTEIRO') return [PERMISSAO_VER_ROTEIROS];
  if (atalho.pedidoVendaId && atalho.itemId) return [PERMISSAO_CADASTRAR, PERMISSAO_EDITAR_PV];
  return atalho.pendenciaId ? [PERMISSAO_CADASTRAR, PERMISSAO_APROVAR_VINCULO] : [PERMISSAO_CADASTRAR];
}

/** `atalhoDoErro` com as permissões que o destino exige, para a tela decidir se mostra o link (fail-closed). */
export function atalhoComPermissao(erro: { readonly codigo: string | null; readonly atalho: AtalhoCadastroDto | null }): AtalhoComPermissao | null {
  const atalho = atalhoDoErro(erro);
  if (atalho === null || erro.atalho === null) return null;
  return { ...atalho, exige: permissoesDoAtalho(erro.atalho) };
}

/** Todas as permissões exigidas estão na sessão? `null` (sessão carregando ou ausente) nega. */
export function permissoesBastam(exige: readonly string[], permissoes: readonly string[] | null): boolean {
  return permissoes !== null && exige.every((permissao) => permissoes.includes(permissao));
}

/**
 * No cadastro pelo código do cliente, a API exige `remessas:gerenciar` quando há pendência a resolver ou quando o código é ligado a um
 * material já cadastrado (P2-CR-01). Avisa antes do envio; `permissoes` vazio = sessão ainda carregando (não avisa).
 */
export function faltaPermissaoDeVinculo(
  modo: ModoNovaPeca,
  pendenciaId: string | null,
  materialExistente: boolean,
  permissoes: readonly string[],
): boolean {
  if (modo !== 'CODIGO_CLIENTE' || permissoes.length === 0) return false;
  return (pendenciaId !== null || materialExistente) && !permissoes.includes(PERMISSAO_APROVAR_VINCULO);
}

/** Mensagem pt-BR dos bloqueios de "Gerar OP"; outros erros seguem com o texto da API. */
export function mensagemDoBloqueio(codigo: string | null, mensagemDaApi: string): string {
  if (codigo === CODIGO_PV_ITEM_SEM_CADASTRO) return MENSAGEM_BLOQUEIO_SEM_CADASTRO;
  if (codigo === CODIGO_PV_ITEM_SEM_ROTEIRO) return MENSAGEM_BLOQUEIO_SEM_ROTEIRO;
  return mensagemDaApi;
}

/** Garante a nota de que a remessa recusada deixou pendência registrada (a API já a inclui; aqui não duplica). */
export function comNotaDePendencia(mensagem: string): string {
  return mensagem.includes(NOTA_PENDENCIA_REGISTRADA) ? mensagem : `${mensagem} ${NOTA_PENDENCIA_REGISTRADA}`;
}

/** "Cadastrar roteiro" do recebimento: leva à ficha da peça do lote (a lista filtrada pelo lote abre a ficha direto). */
export function linkCadastrarRoteiroDoLote(loteId: string): string {
  return `/pcp/roteiros?lote=${encodeURIComponent(loteId)}`;
}

/** O que o item do PV pede: cadastrar a peça (sem Material) ou cadastrar o roteiro (com Material e pendência SEM_ROTEIRO). */
export function atalhoDoItemPv(
  item: Pick<PedidoVendaItemView, 'id' | 'cancelado' | 'materialId' | 'pendencias'>,
  pedidoVendaId: string,
  permissoes: readonly string[],
): { readonly situacao: 'SEM_CADASTRO' | 'SEM_ROTEIRO'; readonly atalho: AtalhoDaTela | null } | null {
  if (item.cancelado) return null;
  if (item.materialId === null) {
    const permitido = permissoes.includes(PERMISSAO_CADASTRAR) && permissoes.includes(PERMISSAO_EDITAR_PV);
    return { situacao: 'SEM_CADASTRO', atalho: permitido ? { href: linkCadastrarPecaDoItem(pedidoVendaId, item.id), rotulo: ROTULO_CADASTRAR_PECA } : null };
  }
  if (item.pendencias.some((pendencia) => pendencia.codigo === 'SEM_ROTEIRO')) {
    return { situacao: 'SEM_ROTEIRO', atalho: permissoes.includes(PERMISSAO_CADASTRAR) ? { href: linkDaFichaDaPeca(item.materialId), rotulo: ROTULO_CADASTRAR_ROTEIRO } : null };
  }
  return null;
}

// ---------------------------------------------------------------------------------------------
// Chamadas à API
// ---------------------------------------------------------------------------------------------

export function itensSemCadastro(busca?: string, clienteId?: string): Promise<ItemSemCadastroDto[]> {
  const params = new URLSearchParams();
  if (busca?.trim()) params.set('busca', busca.trim());
  if (clienteId) params.set('clienteId', clienteId);
  const consulta = params.toString();
  return apiFetch<ItemSemCadastroDto[]>(`${BASE_VENDAS}/itens-sem-cadastro${consulta ? `?${consulta}` : ''}`);
}

async function falhaDeCadastro(resposta: Response): Promise<ErroRoteiroApi> {
  if (resposta.status === 403) {
    return new ErroRoteiroApi(MENSAGEM_SEM_PERMISSAO_CADASTRAR, 403);
  }
  const erro = await lerErroApi(resposta, 'Não foi possível cadastrar a peça. Tente novamente.');
  if (resposta.status === 409 && (erro.codigo === 'PV_CONCORRENCIA' || erro.codigo === 'PV_VERSAO_DESATUALIZADA')) return new ErroRoteiroApi(MENSAGEM_ITEM_ALTERADO, 409);
  return new ErroRoteiroApi(erro.mensagem, resposta.status);
}

/** Cria ou reaproveita a peça, o vínculo do cliente e o roteiro, e liga o item do PV: tudo em uma requisição. */
export async function cadastrarPecaDoItem(pedidoVendaId: string, itemId: string, corpo: CadastrarPecaDoItemDto): Promise<ResultadoCadastrarPecaDto> {
  const resposta = await apiMutate(`${BASE_VENDAS}/pedidos/${encodeURIComponent(pedidoVendaId)}/itens/${encodeURIComponent(itemId)}/cadastrar-peca`, { method: 'POST', body: corpo });
  if (!resposta.ok) throw await falhaDeCadastro(resposta);
  return (await resposta.json()) as ResultadoCadastrarPecaDto;
}

/** Mesmo cadastro a partir do código do cliente (remessa manual recusada ou pendência de item sem conciliação). */
export async function cadastrarPecaDoCodigo(corpo: CadastrarPecaDoCodigoDto): Promise<ResultadoCadastrarPecaDoCodigoDto> {
  const resposta = await apiMutate(`${BASE_VENDAS}/cadastro-peca`, { method: 'POST', body: corpo });
  if (!resposta.ok) throw await falhaDeCadastro(resposta);
  return (await resposta.json()) as ResultadoCadastrarPecaDoCodigoDto;
}
